import React from "react";
import PropTypes from "prop-types";
import { ArrowRight, Minus, Plus, X } from "lucide-react";
import "./ProductDetails.scss";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation, Thumbs, FreeMode } from "swiper/modules";
import { useEffect, useState } from "react";
import SubscriptionToggle from "../cart/SubscriptionToggle";
import { calculatePrice } from "../../utils/priceHandler";
import Button from "../ui/Button";
import ModalWrapper from "../ui/ModalWrapper";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import ProductPrice from "./ProductPrice";
import { useAffiliate } from "../../hooks/useAffiliate";
import { overlayLogoOnImage } from "../../utils/overlayLogoOnImage";
import { Oval } from "react-loader-spinner";

const ProductDetailsModal = ({
  isOpen,
  onClose,
  product,
  image,
  handleAddToCart,
  setAllImages,
  allImages,
  loadingAffiliatePreviewImage = false,
  setAffiliatePreviewImage,
  withSubscriptionToggle = true,
  subscriptionQuantity,
  resultAffiliateImageId,
  customProductsTitles = null
}) => {
  const shouldShow = isOpen && product;
  const productResultImageId = product?.ID ? resultAffiliateImageId?.[product.id] : null;
  const displayTitle = product?.ID && customProductsTitles?.[product?.ID]?.title
    ? customProductsTitles[product.id].title
    : product?.Title;
  const [quantity, setQuantity] = useState(1);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const { affiliate, isLoading: isAffiliateLoading } = useAffiliate();

  useEffect(() => {
    if (!isOpen) {
      setQuantity(1);
      setIsSubscribed(false);
      setAllImages([]);
      if (setAffiliatePreviewImage) {
        setAffiliatePreviewImage(null);
      }
    }
  }, [isOpen]);

  // Apply affiliate logo on back label
  useEffect(() => {
    const prepareImages = async () => {
      if (!product || !image || isAffiliateLoading) return;

      const baseImages = [image, product.label];

      if (affiliate && product.label) {
        try {
          const updatedGalleryImage = await overlayLogoOnImage(product.label, affiliate.logo);
          const updatedImages = [...baseImages];
          updatedImages[1] = updatedGalleryImage;
          setAllImages(updatedImages);
        } catch (err) {
          setAllImages(baseImages);
        }
      } else {
        setAllImages(baseImages);
      }
    };

    prepareImages();
  }, [affiliate, isAffiliateLoading, product?.ID, image]);


  return (
    <>
      <ModalWrapper isOpen={isOpen} onClose={onClose} center={true} className={`product-details-modal ${shouldShow ? 'show' : ''}`}>
        {product && (
          <div className="product-details-inner flex flex-col md:flex-row gap-6 p-6 rounded-20 w-full">
            <div className="gallery-container">
              {(loadingAffiliatePreviewImage || !image || allImages.length === 0) ?
                <Oval
                  visible={true}
                  height={50}
                  width={50}
                  color="#a99cdf"
                  strokeWidth="3"
                  strokeWidthSecondary="3"
                  secondaryColor="#e2e6ff"
                  ariaLabel="oval-loading"
                />
                :
                <div className="flex flex-col items-center justify-start gap-4 h-full max-w-full">
                  <Swiper
                    modules={[Pagination, Thumbs]}
                    slidesPerView={1}
                    spaceBetween={10}
                    pagination={{
                      clickable: true,
                      el: ".product-gallery-pagination",
                    }}
                    thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
                    className="mb-2"
                  >
                    {allImages.map((img, index) => (
                      <SwiperSlide key={`main-${index}`}>
                        <div
                          className="w-full rounded-10 overflow-hidden border cursor-pointer"
                          onClick={() => {
                            setLightboxIndex(index);
                            setIsLightboxOpen(true);
                          }}
                        >
                          <img
                            src={img}
                            alt={`Product image ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>

                  <Swiper
                    onSwiper={setThumbsSwiper}
                    spaceBetween={10}
                    slidesPerView={4}
                    freeMode={true}
                    watchSlidesProgress={true}
                    modules={[FreeMode, Thumbs]}
                    className="thumbs-swiper"
                  >
                    {allImages.map((img, index) => (
                      <SwiperSlide key={`thumb-${index}`}>
                        <div className="thumb-slide">
                          <img
                            src={img}
                            alt={`Thumbnail ${index + 1}`}
                            className="w-full h-auto rounded border cursor-pointer object-cover"
                          />
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              }
            </div>

            <div className="flex-1 flex flex-col gap-3">
              <div className="flex-1 flex flex-col gap-3">
                <p className="text-mid mb-0">{displayTitle} | <span className="capitalize">{product.flavor}</span></p>
                <p className="product-desctiption">{product.description}</p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-4">
                  {!subscriptionQuantity &&
                    <div className="flex items-center gap-2">
                      <span>Quantity:</span>
                      <div className="quantity-controls flex items-center gap-2 border rounded-lg p-1">
                        <button
                          onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
                          className="quantity-btn px-2 py-1"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="text-lg font-semibold">{quantity}</span>
                        <button
                          onClick={() => setQuantity((prev) => prev + 1)}
                          className="quantity-btn px-2 py-1"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                    </div>
                  }
                  <ProductPrice price={product.price} isSubscribed={isSubscribed} />
                </div>

                {withSubscriptionToggle &&
                  <SubscriptionToggle
                    isSubscribed={isSubscribed}
                    onToggle={() => setIsSubscribed((prev) => !prev)}
                    className="mb-4"
                  />
                }

                <Button
                  variant="filled"
                  className="justify-between"
                  type="button"
                  onClick={() => handleAddToCart(product, isSubscribed, quantity, productResultImageId, customProductsTitles?.[product?.ID]?.title)}
                >
                  Add to Cart
                  <ArrowRight size={27} />
                </Button>
              </div>
            </div>
          </div>
        )}
      </ModalWrapper>

      <Lightbox
        open={isLightboxOpen}
        close={() => setIsLightboxOpen(false)}
        slides={allImages.map((img) => ({ src: img }))}
        index={lightboxIndex}
        carousel={{ finite: true }}
      />
    </>
  );
};

ProductDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  product: PropTypes.object,
  image: PropTypes.string,
  handleAddToCart: PropTypes.func,
  setAllImages: PropTypes.func,
  allImages: PropTypes.array,
  loadingAffiliatePreviewImage: PropTypes.bool,
  setAffiliatePreviewImage: PropTypes.func,
  withSubscriptionToggle: PropTypes.bool,
  subscriptionQuantity: PropTypes.object,
  resultAffiliateImageId: PropTypes.object,
  customProductsTitles: PropTypes.object
};

export default ProductDetailsModal;
